import React from 'react'
import { Formik, Form, Field } from 'formik'
import {
  Button,
  Input,
  useToast,
  GridItem,
  Grid,
  Heading,
  Box,
  Select,
  Flex,
  Switch,
} from '@chakra-ui/react'

import DatePicker from 'components/datePicker/DatePicker'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'

import xhr from 'xhr'
import QuillEditor from 'components/editorQuill'

import ClozeEditor from './interactiveEditors/ClozeEditor'
import MatchingEditor from './interactiveEditors/MatchingEditor'

const EMPTY_CLOZE = { version: 1, prompt_html: '', blanks: [] }
const EMPTY_MATCHING = {
  version: 1,
  left_label: '',
  right_label: '',
  left: [],
  right: [],
  pairs: [],
}

const DEFAULT_PROMPTS = {
  cloze:
    '<p><strong>Lee con atención y completa cada espacio en blanco con la palabra correcta.</strong></p>',
  matching:
    '<p><strong>Empareja cada elemento de la columna izquierda con el correspondiente de la derecha. Toca un ítem y luego su par para confirmar.</strong></p>',
}

function isQuillEmpty(html) {
  const s = String(html || '').trim()
  return s === '' || s === '<p></p>' || s === '<p><br></p>'
}

function isDefaultPrompt(html) {
  const s = String(html || '').trim()
  return Object.values(DEFAULT_PROMPTS).some((d) => d.trim() === s)
}

function validateInteractive(type, config) {
  if (!type) return null
  if (type === 'cloze') {
    if (!config || !Array.isArray(config.blanks) || config.blanks.length === 0) {
      return 'Marca al menos un espacio en el enunciado.'
    }
    for (const b of config.blanks) {
      if (b.mode === 'select') {
        const opts = (b.options || []).filter((o) => String(o || '').trim())
        if (opts.length < 2) {
          return `El espacio [${b.id}] necesita al menos 2 opciones.`
        }
        if (!b.accepted_answers || !b.accepted_answers[0]) {
          return `El espacio [${b.id}] necesita una opción marcada como correcta.`
        }
        if (!opts.includes(b.accepted_answers[0])) {
          return `El espacio [${b.id}]: la opción correcta debe coincidir con una de las opciones.`
        }
      } else {
        if (!b.accepted_answers || b.accepted_answers.length === 0) {
          return `El espacio [${b.id}] necesita al menos una respuesta válida.`
        }
      }
    }
  }
  if (type === 'matching') {
    if (!config) return 'Configura los pares de emparejamiento.'
    if (!config.left_label || !config.right_label) {
      return 'Define los nombres de las dos columnas.'
    }
    if (!Array.isArray(config.left) || config.left.length < 2) {
      return 'Agrega al menos 2 ítems en la columna izquierda.'
    }
    if (!Array.isArray(config.right) || config.right.length < 2) {
      return 'Agrega al menos 2 ítems en la columna derecha.'
    }
    const missingPair = config.left.find(
      (it) => !config.pairs.some((p) => p.leftId === it.id)
    )
    if (missingPair) {
      return 'Cada ítem de la columna izquierda debe emparejarse con uno de la derecha.'
    }
  }
  return null
}

export default function InsertActivity(props) {
  const { dataMaterials, select, setIsOpen } = props
  const params = useParams()

  const [activeAi, setActiveAi] = React.useState(false)
  const toasts = useToast()

  const [dateFilter, setDateFilter] = React.useState(null)

  const [dateFilterEnd, setDateFilterEnd] = React.useState(null)

  const handleDateChange = (date) => {
    setDateFilter(date)
  }

  const handleDateChangeEnd = (date) => {
    setDateFilterEnd(date)
  }

  const [loadingButton, setLoadingButton] = React.useState(false)
  const [inputTitle, setInputTitle] = React.useState('')
  const [textContent, setTextContent] = React.useState('')
  const [videoContent, setVideoContent] = React.useState('')
  const [interactiveType, setInteractiveType] = React.useState('')
  const [clozeConfig, setClozeConfig] = React.useState(EMPTY_CLOZE)
  const [matchingConfig, setMatchingConfig] = React.useState(EMPTY_MATCHING)

  const handleVideo = async (values, action) => {
    console.log(values)
    try {
      setLoadingButton(true)
      if (textContent == '') return alert('el texto está vacio')
      if (inputTitle == '') return alert('el titulo está vacio')
      const activeConfig =
        interactiveType === 'cloze'
          ? clozeConfig
          : interactiveType === 'matching'
          ? matchingConfig
          : null
      const interactiveError = validateInteractive(interactiveType, activeConfig)
      if (interactiveError) {
        alert(interactiveError)
        return
      }
      const responseActivity = await xhr.post(`/activities/`, {
        data: {
          title: inputTitle,
          content: textContent,
          subject: params.idSubject.id ? params.idSubject.id : params.idSubject,
          video: videoContent,
          ai: activeAi,
          interactive_type: interactiveType || null,
          interactive_config: activeConfig,
        },
      })
      setTextContent('')
      setInputTitle('')
      const response = await xhr.post(`/materials/`, {
        data: {
          ...values,
          activity: responseActivity.data.data.id,
          module: params.idModule,
          type: select,
          enabled_date: dateFilter,
          enabled_date_end: dateFilterEnd,
        },
      })
      const getPopulate = await xhr(
        `/materials/${response.data.data.id}?populate=*`
      )
      toasts({
        title: 'El material se creo correctamente!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      dataMaterials(getPopulate.data.data)
      setIsOpen(false)
      action.resetForm()
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingButton(false)
    }
  }

  const RequerimentSchema = Yup.object().shape({
    title: Yup.string().required('Requerido'),
    order: Yup.string().required('Requerido'),
    duration: Yup.string().required('Requerido'),
  })

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          title: '',
          order: '',
          duration: '',
        }}
        validationSchema={RequerimentSchema}
        onSubmit={handleVideo}
      >
        <Form>
          <Grid templateColumns="repeat(12, 1fr)" gap={6}>
            <GridItem colSpan={4}>
              <Box marginTop="20px">
                <Heading use="h3" width="100%">
                  Material
                </Heading>
              </Box>
              <Grid marginTop="20px" gap={4}>
                <GridItem colSpan={6}>
                  <Box>Título</Box>
                  <Field marginTop="20px" as={Input} name="title" />
                </GridItem>

                <GridItem colSpan={3}>
                  <Box>Orden</Box>
                  <Field
                    marginTop="20px"
                    as={Input}
                    type="number"
                    name="order"
                  />
                </GridItem>

                <GridItem colSpan={3}>
                  <Box>Duración</Box>
                  <Field
                    marginTop="20px"
                    as={Input}
                    type="number"
                    name="duration"
                  />
                </GridItem>

                <GridItem colSpan={6}>
                  <Box color="#636363">Periodo</Box>
                  <Field marginTop="20px" name="period" as={Select}>
                    <option value=""></option>
                    <option value="primero">primer</option>
                    <option value="segundo">segundo</option>
                    <option value="tercero">tercer</option>
                    <option value="cuarto">cuarto</option>
                  </Field>
                </GridItem>
                <GridItem colSpan={12}>
                  <br />
                  <Box color="#636363">Fecha Inicio</Box>
                  <DatePicker
                    date={dateFilter}
                    placeholder="Fecha Inicio"
                    onChange={handleDateChange}
                  />
                  <br />
                  <Box color="#636363">Fecha Fin</Box>
                  <DatePicker
                    date={dateFilterEnd}
                    placeholder="Fecha Fin"
                    onChange={handleDateChangeEnd}
                  />

                  <br />
                </GridItem>
              </Grid>
            </GridItem>

            <GridItem
              colSpan={8}
              marginTop="30px"
              borderLeft="1px solid"
              borderColor="gray.200"
              paddingLeft="20px"
            >
              <Box marginBottom="20px" position="relative" top="-10px">
                <Heading use="h3" width="100%">
                  Actividad
                </Heading>
              </Box>

              <Box
                p="16px"
                bg="#f3f1ff"
                borderLeft="3px solid #574feb"
                borderRadius="6px"
              >
                <Box fontSize="14px" fontWeight="500" color="#3c3489" mb="8px">
                  Tipo interactivo
                </Box>
                <Select
                  bg="white"
                  value={interactiveType}
                  onChange={(e) => {
                    const next = e.target.value
                    setInteractiveType(next)
                    if (next) {
                      setActiveAi(true)
                      if (
                        DEFAULT_PROMPTS[next] &&
                        (isQuillEmpty(textContent) || isDefaultPrompt(textContent))
                      ) {
                        setTextContent(DEFAULT_PROMPTS[next])
                      }
                    }
                  }}
                >
                  <option value="">Ninguno (texto / archivos)</option>
                  <option value="cloze">Completar espacios (Cloze)</option>
                  <option value="matching">Emparejar columnas</option>
                </Select>
                <Box fontSize="12px" color="gray.500" mt="6px">
                  Si eliges un tipo, los estudiantes lo resuelven inline y la nota se calcula sola. Si dejas <em>Ninguno</em>, la actividad sigue funcionando con texto y archivos.
                </Box>
              </Box>

              <Box mt="16px" mb="10px">
                <Flex gap={3} alignItems="center">
                  <Box>
                    {interactiveType
                      ? 'Incluir retroalimentación con IA?'
                      : 'Calificable con AI?'}
                  </Box>
                  <Switch
                    isChecked={activeAi}
                    onChange={(e) => setActiveAi(!activeAi)}
                    id="email-alerts"
                  />
                </Flex>
                {interactiveType && (
                  <Box fontSize="12px" color="gray.500" mt="6px">
                    El score se calcula automáticamente. Activa esta opción para generar también un mensaje de retroalimentación con IA bajo el nombre del profesor.
                  </Box>
                )}
              </Box>

              <Box mt="20px">Título</Box>
              <Input
                marginTop="10px"
                as={Input}
                name="titleActivity"
                value={inputTitle}
                onChange={(e) => setInputTitle(e.target.value)}
              />

              <Box mt="20px">
                {interactiveType ? 'Instrucciones para el estudiante' : 'Texto'}
              </Box>
              <QuillEditor value={textContent} setValue={setTextContent} />

              {interactiveType === 'cloze' && (
                <Box mt="16px">
                  <ClozeEditor value={clozeConfig} onChange={setClozeConfig} />
                </Box>
              )}

              {interactiveType === 'matching' && (
                <Box mt="16px">
                  <MatchingEditor value={matchingConfig} onChange={setMatchingConfig} />
                </Box>
              )}

              <Box marginTop="20px" color="#636363">
                Video
              </Box>
              <Input
                marginTop="10px"
                as={Input}
                name="video"
                value={videoContent}
                onChange={(e) => setVideoContent(e.target.value)}
              />
            </GridItem>
          </Grid>

          <Button
            type="submit"
            colorScheme="#574feb"
            bg="#574feb"
            marginY="10px"
            marginX="10px"
            width="140px"
            isLoading={loadingButton}
            disabled={loadingButton}
          >
            Crear
          </Button>
        </Form>
      </Formik>
      <style jsx global>{`
        .rdw-editor-wrapper {
          min-height: 600px;
          border: 1px solid #d0cbcb;
        }
        .rdw-editor-main {
          padding: 10px;
        }

        .public-DraftEditor-content {
          height: 600px !important;
        }
      `}</style>
    </div>
  )
}
